import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addMonths,
  differenceInCalendarDays,
  differenceInMonths,
  format,
  parseISO,
  startOfDay,
} from 'date-fns';
import styled from '@emotion/styled';

import { useTheme } from '@/theme';
import { Button, Flex, Input, Text } from '@/components/ui';
import { InputStyles } from '@/components/ui/common/input/InputStyles';
import { useNotificationStore } from '@/store/notificationStore';
import { useModalStore } from '@/store/modalStore';
import { useAppStore } from '@/store/loadingStore';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { parseCompanyApiError } from '@/utils/errors';
import { useCreateSubscription, useGetSubscription } from '@/api/company/useSubscription';

const PRESET_MONTHS = [1, 3, 6, 12];

type EndDateMode = 'preset' | 'custom';

const DateInput = styled.input`
  width: 148px;
  flex-shrink: 0;
  height: 26px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => theme.spacing[2]}px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus {
    border: 1px solid ${({ theme }) => theme.colors.border.indicator};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.border.indicator}33`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
    opacity: 0.85;
  }
`;

const SettingsControlRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  width: 100%;
  min-height: 26px;
`;

const CompactPresetButton = styled(Button)`
  height: 26px;
  min-height: 26px;
  padding: 0 ${({ theme }) => theme.spacing[4]}px;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  line-height: 1;
  flex-direction: row;
  justify-content: center;
`;

const DeviceLimitWrap = styled.div`
  position: relative;
  width: 88px;
  flex-shrink: 0;
`;

const DeviceLimitInput = styled.input`
  width: 100%;
  height: 26px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.surface.control.default};
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) =>
    `${theme.spacing[2]}px ${theme.spacing[14]}px ${theme.spacing[2]}px ${theme.spacing[2]}px`};
  text-align: right;
  outline: none;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus {
    border: 1px solid ${({ theme }) => theme.colors.border.indicator};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.border.indicator}33`};
  }
`;

const DeviceLimitUnit = styled.span`
  position: absolute;
  right: ${({ theme }) => theme.spacing[2]}px;
  top: 50%;
  transform: translateY(-50%);
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  pointer-events: none;
`;

interface CompanySubscriptionSettingsFormProps {
  companyId: number;
  isActive: boolean;
  onSaved?: () => void;
}

const CompanySubscriptionSettingsForm: React.FC<CompanySubscriptionSettingsFormProps> = ({
  companyId,
  isActive,
  onSaved,
}) => {
  const { t } = useTranslation(['company', 'common', 'device']);
  const { theme } = useTheme();
  const { showSuccess, showError } = useNotificationStore();
  const { showConfirm } = useModalStore();
  const { showLoading, hideLoading } = useAppStore();

  const { data, refetch } = useGetSubscription(companyId, {
    enabled: !!companyId && isActive,
  });
  const createMutation = useCreateSubscription(companyId);

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const [endDate, setEndDate] = useState<string>(format(addMonths(new Date(), 1), 'yyyy-MM-dd'));
  const [deviceLimit, setDeviceLimit] = useState<number>(10);
  const [memo, setMemo] = useState<string>('');
  const [endDateMode, setEndDateMode] = useState<EndDateMode>('preset');
  const [activePresetMonths, setActivePresetMonths] = useState<number | null>(1);

  const periodStart = useMemo(() => startOfDay(new Date()), []);
  const current = data?.current ?? null;
  const history = data?.history ?? [];

  useEffect(() => {
    if (!isActive) return;
    if (current) {
      setEndDate(current.periodEnd);
      setDeviceLimit(current.deviceLimit);
      setEndDateMode('custom');
      setActivePresetMonths(null);
      setMemo('');
      return;
    }
    setEndDateMode('preset');
    setActivePresetMonths(1);
    setEndDate(format(addMonths(new Date(), 1), 'yyyy-MM-dd'));
    setDeviceLimit(10);
    setMemo('');
  }, [isActive, current, companyId]);

  const selectPresetMonths = (months: number) => {
    setEndDateMode('preset');
    setActivePresetMonths(months);
    setEndDate(prev => {
      const parsed = parseISO(prev);
      const base =
        !prev || Number.isNaN(parsed.getTime()) || parsed < periodStart ? periodStart : parsed;
      return format(addMonths(base, months), 'yyyy-MM-dd');
    });
  };

  const selectCustomEndDate = () => {
    setEndDateMode('custom');
    setActivePresetMonths(null);
  };

  const periodUntilEndLabel = useMemo(() => {
    if (!endDate) return '';
    const end = parseISO(endDate);
    if (Number.isNaN(end.getTime()) || end <= periodStart) return '';
    const months = differenceInMonths(end, periodStart);
    const afterMonths = addMonths(periodStart, months);
    const days = Math.max(differenceInCalendarDays(end, afterMonths), 0);
    const parts: string[] = [];
    if (months > 0) {
      parts.push(t(i18nKeys.company.subscription_duration_months, { count: months }));
    }
    if (days > 0) {
      parts.push(t(i18nKeys.company.subscription_duration_days, { count: days }));
    }
    if (parts.length === 0) {
      parts.push(t(i18nKeys.company.subscription_duration_days, { count: 0 }));
    }
    return `(${parts.join(' ')})`;
  }, [endDate, periodStart, t]);

  const handleSave = () => {
    if (!endDate) {
      showError(i18nKeys.company.error_subscription_end_date_required);
      return;
    }

    const isRenewal = history.length > 0;
    const confirmTitle = isRenewal
      ? t(i18nKeys.company.subscription_renew)
      : t(i18nKeys.company.subscription_register);

    showConfirm(
      confirmTitle,
      t(i18nKeys.company.subscription_save_confirm, {
        endDate,
        deviceLimit,
      }),
      () => {
        void (async () => {
          try {
            showLoading();
            await createMutation.mutateAsync({
              endDate,
              deviceLimit,
              memo: memo.trim() || undefined,
            });
            await refetch();
            showSuccess('company:subscription_save_success');
            setMemo('');
            onSaved?.();
          } catch (error: unknown) {
            const e = parseCompanyApiError(error);
            showError(e.title, e.message);
          } finally {
            hideLoading();
          }
        })();
      },
      undefined,
      'primary'
    );
  };

  return (
    <Flex direction="column" gap={4} align="stretch">
      <InputStyles.InputContainer compact>
        <SettingsControlRow style={{ width: '100%' }} data-tour="subscription-duration-preset">
          {PRESET_MONTHS.map(m => (
            <CompactPresetButton
              key={m}
              variant={
                endDateMode === 'preset' && activePresetMonths === m ? 'primary' : 'tertiary'
              }
              width="auto"
              onClick={() => selectPresetMonths(m)}
            >
              {t(i18nKeys.company.subscription_duration_months, { count: m })}
            </CompactPresetButton>
          ))}
          <CompactPresetButton
            variant={endDateMode === 'custom' ? 'primary' : 'tertiary'}
            width="auto"
            onClick={selectCustomEndDate}
          >
            {t(i18nKeys.company.subscription_custom)}
          </CompactPresetButton>

          <Text
            size="sm"
            color={theme.colors.text.secondary}
            style={{ flexShrink: 0, lineHeight: '26px' }}
          >
            {t(i18nKeys.company.subscription_end_date)}
          </Text>
          <DateInput
            id={`subscription-settings-${companyId}`}
            data-tour="subscription-end-date"
            type="date"
            value={endDate}
            min={today}
            disabled={endDateMode !== 'custom'}
            onChange={e => setEndDate(e.target.value)}
          />
          {periodUntilEndLabel && (
            <Text
              size="sm"
              color={theme.colors.text.secondary}
              style={{ flexShrink: 0, lineHeight: '26px' }}
            >
              {periodUntilEndLabel}
            </Text>
          )}

          <DeviceLimitWrap data-tour="subscription-device-limit">
            <DeviceLimitInput
              type="text"
              inputMode="numeric"
              value={String(deviceLimit)}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '');
                if (digits === '') {
                  setDeviceLimit(0);
                  return;
                }
                setDeviceLimit(parseInt(digits, 10));
              }}
              aria-label={t(i18nKeys.company.subscription_device_limit)}
            />
            <DeviceLimitUnit>{t(i18nKeys.device.device)}</DeviceLimitUnit>
          </DeviceLimitWrap>
        </SettingsControlRow>
      </InputStyles.InputContainer>

      <div data-tour="subscription-memo">
        <Input
          label={t(i18nKeys.company.subscription_memo)}
          value={memo}
          onChange={setMemo}
          placeholder={t(i18nKeys.company.subscription_memo_placeholder)}
          fullWidth
        />
      </div>

      <Flex gap={3} justify="end" align="center" data-tour="subscription-save">
        <Button variant="primary" onClick={handleSave} disabled={createMutation.isPending}>
          {current
            ? t(i18nKeys.company.subscription_renew)
            : t(i18nKeys.company.subscription_register)}
        </Button>
      </Flex>
    </Flex>
  );
};

export default CompanySubscriptionSettingsForm;
